/** Which colour theme this browser is using.
 *
 *  Five: three light, two dark. The stored value is the theme's KEY, and `default`
 *  keeps that key even though it now shows as "Paper" - renaming the key would have
 *  silently reset the theme for everybody who had already chosen one, because an
 *  unrecognised stored value falls back to the default.
 *
 *  Per-viewer and local by design: it is a preference about a screen, not a fact
 *  about the firm, so it belongs in `localStorage` and never in the database. Two
 *  people sharing an account can want different ones.
 */
export const THEMES = [
  ['default', 'Paper', 'warm paper, deep navy'],
  ['slate', 'Slate', 'cool and flat, deep teal'],
  ['ledger', 'Ledger', 'warm stone page, deep green'],
  ['midnight', 'Midnight', 'dimmed blue-grey'],
  ['graphite', 'Graphite', 'dimmed warm grey, eucalyptus'],
] as const

export type Theme = typeof THEMES[number][0]

const KEY = 'tm.theme'
const NAMES = THEMES.map(([v]) => v) as readonly string[]

/** The stored choice, or the default.
 *
 *  Wrapped, because `localStorage` throws rather than returning null in a few real
 *  situations - Safari's private mode historically, and any browser set to block
 *  site data - and a colour preference must never be the thing that stops the app
 *  rendering.
 */
export function readTheme(): Theme {
  try {
    const got = localStorage.getItem(KEY)
    return (got && NAMES.includes(got) ? got : 'default') as Theme
  } catch {
    return 'default'
  }
}

/** Paint it. The attribute goes on `<html>`, which is where the variable blocks in
 *  `index.css` are scoped - and `default` is written explicitly rather than removed,
 *  so the attribute always says what is showing. */
export function applyTheme(t: Theme): void {
  document.documentElement.setAttribute('data-theme', t)
  try {
    localStorage.setItem(KEY, t)
  } catch {
    // Unstorable is not unusable: the theme still applies for this page.
  }
}
